/**
 * iter167 — Dialog d'import d'OR depuis rapport Auto-I (PDF) ou screenshots Guido (images).
 *
 * Deux modes :
 *   - source="auto-i" : upload d'UN PDF → parsing 5 sous-totaux HT.
 *   - source="guido"  : upload de 1 à 3 images (screenshots iPhone) → détail article par article.
 *
 * Flow :
 *   1) Sélection fichier(s) → POST /api/or/import/{source}
 *   2) Preview éditable (client / véhicule / lignes)
 *   3) Confirmation → POST /api/or/import/confirm → navigate vers /atelier/or/{id}
 */
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Upload, FileText, Camera, Loader2, User, Car, ShieldCheck,
  AlertCircle, CheckCircle2, Trash2, Plus,
} from "lucide-react";
import api from "@/lib/api";

const fmt = (n) => Number(n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function OrImportDialog({ open, onClose, source }) {
  // source = 'auto-i' | 'guido'
  const [step, setStep] = useState("upload"); // upload | preview | submitting
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState("");
  const [dragOver, setDragOver] = useState(false);  // iter180 — état drag&drop
  // iter270 — Dossier ouvert déjà existant pour ce véhicule (doublon d'import)
  const [doublon, setDoublon] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const reset = () => {
    setStep("upload");
    setFiles([]);
    setPreview(null);
    setProgress("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const cfg = source === "auto-i"
    ? { title: "Importer un rapport Auto-I", icon: FileText, accept: ".pdf", multiple: false,
        hint: "PDF du rapport de calcul Auto-I (Helvetic Motion, Allianz, AXA…). 1 fichier PDF." }
    : { title: "Importer des screenshots Guido", icon: Camera, accept: "image/*", multiple: true,
        hint: "1 à 3 captures d'écran iPhone (intervention + infos véhicule)." };

  const onFilesSelected = (fList) => {
    const arr = Array.from(fList || []);
    // iter180 — Filtrage par type MIME/extension pour éviter les dépôts erronés
    let filtered = arr;
    if (source === "auto-i") {
      filtered = arr.filter((f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name));
      if (filtered.length === 0 && arr.length > 0) {
        toast.error("Auto-I attend un fichier PDF.");
        return;
      }
    } else {
      filtered = arr.filter((f) => f.type.startsWith("image/") || /\.(png|jpg|jpeg|heic|webp)$/i.test(f.name));
      if (filtered.length === 0 && arr.length > 0) {
        toast.error("Guido attend des images (PNG/JPG/HEIC).");
        return;
      }
    }
    if (source === "auto-i" && filtered.length > 1) {
      toast.error("Auto-I : un seul PDF à la fois");
      return;
    }
    if (source === "guido" && filtered.length > 3) {
      toast.error("Guido : maximum 3 screenshots");
      return;
    }
    setFiles(filtered);
  };

  // iter180 — Drag & drop natif HTML5
  const onDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const dropped = e.dataTransfer?.files;
    if (dropped && dropped.length) onFilesSelected(dropped);
  };

  const doUpload = async () => {
    if (!files.length) return toast.error("Aucun fichier sélectionné");
    setStep("submitting");
    setProgress(source === "auto-i" ? "Analyse du rapport Auto-I par l'IA…" : "Lecture des screenshots Guido par l'IA…");
    try {
      const form = new FormData();
      if (source === "auto-i") {
        form.append("file", files[0]);
      } else {
        files.forEach((f) => form.append("files", f));
      }
      const { data } = await api.post(`/or/import/${source}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setPreview(data);
      setStep("preview");
    } catch (e) {
      toast.error(`Import échoué : ${e.response?.data?.detail || e.message}`);
      setStep("upload");
    }
  };

  const doConfirm = async (forceDoublon = false) => {
    if (!preview) return;
    setStep("submitting");
    setDoublon(null);
    setProgress("Création du client, du véhicule et de l'OR…");
    try {
      const { data } = await api.post("/or/import/confirm", { ...preview, force_doublon: forceDoublon });
      toast.success(`OR ${data.or_numero} créé (${data.lignes_count} ligne${data.lignes_count > 1 ? "s" : ""})`);
      handleClose();
      navigate(`/atelier/or/${data.or_id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // iter270 — Doublon détecté : on propose de reprendre le dossier existant
      if (e.response?.status === 409 && detail?.code === "doublon_or") {
        setDoublon(detail);
        setStep("preview");
        return;
      }
      toast.error(`Création OR échouée : ${typeof detail === "string" ? detail : e.message}`);
      setStep("preview");
    }
  };

  const Icon = cfg.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`or-import-dialog-${source}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-[#52a675]" />
            {cfg.title}
          </DialogTitle>
        </DialogHeader>

        {/* --- Step 1 : Upload --- */}
        {step === "upload" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">{cfg.hint}</p>
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
              onDragEnter={onDragEnter}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              className={`w-full border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                dragOver
                  ? "border-[#3d8a5c] bg-[#e7f5ed] ring-2 ring-[#52a675] ring-offset-1"
                  : "border-gray-300 hover:border-[#52a675] hover:bg-[#f0f9f4]"
              }`}
              data-testid={`or-import-picker-${source}`}
            >
              <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? "text-[#3d8a5c]" : "text-gray-400"}`} />
              <p className="text-sm text-gray-700 font-semibold">
                {dragOver
                  ? "Relâchez pour importer"
                  : files.length === 0
                    ? "Glissez le/les fichier(s) ici ou cliquez pour parcourir"
                    : `${files.length} fichier${files.length > 1 ? "s" : ""} sélectionné${files.length > 1 ? "s" : ""}`}
              </p>
              <p className="text-xs text-gray-400 mt-1">{cfg.accept}</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={cfg.accept}
              multiple={cfg.multiple}
              className="hidden"
              onChange={(e) => onFilesSelected(e.target.files)}
              data-testid={`or-import-file-input-${source}`}
            />
            {files.length > 0 && (
              <ul className="border rounded bg-gray-50 divide-y divide-gray-200">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="truncate">{f.name}</span>
                    <span className="text-xs text-gray-500 ml-2">{(f.size / 1024).toFixed(0)} kB</span>
                  </li>
                ))}
              </ul>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Annuler</Button>
              <Button
                onClick={doUpload}
                disabled={files.length === 0}
                className="bg-[#52a675] hover:bg-[#3d8a5c]"
                data-testid={`or-import-analyze-${source}`}
              >
                Lancer l&apos;analyse IA
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* --- Step submitting --- */}
        {step === "submitting" && (
          <div className="py-12 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-[#52a675] animate-spin" />
            <p className="text-sm text-gray-600">{progress}</p>
            <p className="text-xs text-gray-400">Cela peut prendre 15-40 secondes.</p>
          </div>
        )}

        {/* --- Step 2 : Preview --- */}
        {step === "preview" && preview && (
          <PreviewForm
            preview={preview}
            setPreview={setPreview}
            source={source}
            onCancel={() => setStep("upload")}
            onConfirm={() => doConfirm(false)}
            doublon={doublon}
            onForceDoublon={() => doConfirm(true)}
            onIgnoreDoublon={() => setDoublon(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// PreviewForm — édition client / véhicule / lignes avant création
// -----------------------------------------------------------------------------
function PreviewForm({ preview, setPreview, source, onCancel, onConfirm, doublon, onForceDoublon, onIgnoreDoublon }) {
  const { client, vehicule, or_lignes, or_meta, totaux_hints } = preview;
  // iter186 — Permettre de switcher / créer un client depuis le preview import.
  // iter264 — Si le document ne permet pas d'identifier le client (ou ne donne que
  // le nom du garage), la recherche est ouverte d'emblée : c'est l'utilisateur qui choisit.
  const clientManuel = client.match_type === "manual" || client.requires_manual;
  const [clientSearch, setClientSearch] = useState({
    open: clientManuel, q: "", items: [], loading: false,
  });

  const doSearchClient = async (q) => {
    setClientSearch((p) => ({ ...p, q, loading: true }));
    if (!q || q.length < 2) {
      setClientSearch((p) => ({ ...p, items: [], loading: false }));
      return;
    }
    try {
      const { data } = await api.get("/clients", { params: { q, limit: 20 } });
      setClientSearch((p) => ({ ...p, items: data.items || data || [], loading: false }));
    } catch {
      setClientSearch((p) => ({ ...p, items: [], loading: false }));
    }
  };

  const applyClientMatch = (c) => {
    setPreview({
      ...preview,
      client: {
        match_type: "existing",
        match_id: c.id,
        match_reason: "Sélection manuelle",
        existing: c,
        payload: {
          prenom: c.prenom || "",
          nom: c.nom || "",
          raison_sociale: c.raison_sociale || "",
          telephone: c.tel_principal || c.telephone || "",
          email: c.email || "",
          adresse: c.adresse || "",
          code_postal: c.code_postal || "",
          ville: c.ville || "",
          pays: c.pays || "CH",
          type_client: c.type_client || "particulier",
        },
      },
    });
    setClientSearch({ open: false, q: "", items: [], loading: false });
  };

  const switchToNewClient = () => {
    setPreview({
      ...preview,
      client: {
        match_type: "new",
        match_id: null,
        match_reason: "Nouveau client",
        existing: null,
        payload: {
          prenom: "", nom: "", raison_sociale: "",
          telephone: "", email: "", adresse: "",
          code_postal: "", ville: "", pays: "CH",
          type_client: "particulier",
        },
      },
    });
    setClientSearch({ open: false, q: "", items: [], loading: false });
  };

  const updateClient = (key, value) =>
    setPreview({
      ...preview,
      client: { ...client, payload: { ...client.payload, [key]: value } },
    });

  const updateVehicule = (key, value) =>
    setPreview({
      ...preview,
      vehicule: { ...vehicule, payload: { ...vehicule.payload, [key]: value } },
    });

  const updateLigne = (idx, key, value) => {
    const next = [...or_lignes];
    next[idx] = { ...next[idx], [key]: value };
    setPreview({ ...preview, or_lignes: next });
  };

  const deleteLigne = (idx) => {
    setPreview({ ...preview, or_lignes: or_lignes.filter((_, i) => i !== idx) });
  };

  const addLigne = () => {
    setPreview({
      ...preview,
      or_lignes: [...or_lignes, {
        libelle: "", quantite: 1, prix_unitaire_ht: 0, unite: "forfait",
        type_ligne: "article", tva_pct: 8.1, rabais_pct: 0, rabais_chf: 0,
      }],
    });
  };

  const totalHT = or_lignes.reduce((sum, l) => {
    if (l.type_ligne === "texte") return sum;
    return sum + (Number(l.quantite || 0) * Number(l.prix_unitaire_ht || 0));
  }, 0);

  return (
    <div className="space-y-5">
      {/* Client */}
      <section className="border rounded-lg p-3 bg-white" data-testid="or-import-preview-client">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <User className="w-4 h-4 text-[#52a675]" /> Client
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            {client.match_type === "existing" ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Client existant · {client.match_reason}
              </Badge>
            ) : clientManuel ? (
              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 flex items-center gap-1" data-testid="or-import-client-manual-badge">
                <AlertCircle className="w-3 h-3" /> Client à sélectionner
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Nouveau client
              </Badge>
            )}
            {/* iter186 — Bouton pour rechercher un autre client / créer un nouveau depuis le preview */}
            <Button
              type="button" size="sm" variant="outline"
              onClick={() => setClientSearch((p) => ({ ...p, open: !p.open }))}
              className="text-xs h-7 px-2"
              data-testid="or-import-change-client-toggle"
              title="Ce n'est pas le bon client ? Chercher un autre ou en créer un nouveau"
            >
              {clientSearch.open ? "Fermer" : "Changer de client"}
            </Button>
          </div>
        </div>
        {clientManuel && (
          <p className="mb-3 text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded p-2" data-testid="or-import-client-manual-hint">
            {client.match_reason || "Client non identifié dans le document."} Choisissez le client du dossier
            ci-dessous (ou créez-le) avant de créer l&apos;OR.
          </p>
        )}
        {clientSearch.open && (
          <div className="mb-3 p-2.5 bg-gray-50 border border-gray-200 rounded" data-testid="or-import-client-switcher">
            <div className="flex gap-2 mb-2">
              <Input
                autoFocus
                placeholder="Chercher par nom, prénom, plaque, tel…"
                value={clientSearch.q}
                onChange={(e) => doSearchClient(e.target.value)}
                data-testid="or-import-client-search"
                className="text-sm"
              />
              <Button
                type="button" variant="outline" size="sm"
                onClick={switchToNewClient}
                data-testid="or-import-client-new"
                className="whitespace-nowrap"
                title="Créer un nouveau client (efface la sélection actuelle)"
              >
                + Nouveau
              </Button>
            </div>
            {clientSearch.loading && <p className="text-xs text-gray-500 py-2">Recherche…</p>}
            {!clientSearch.loading && clientSearch.q.length >= 2 && clientSearch.items.length === 0 && (
              <p className="text-xs text-gray-500 py-2">Aucun résultat. Clique &quot;+ Nouveau&quot; pour créer.</p>
            )}
            {clientSearch.items.length > 0 && (
              <div className="max-h-48 overflow-y-auto divide-y divide-gray-200 bg-white rounded border border-gray-100">
                {clientSearch.items.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => applyClientMatch(c)}
                    className="w-full text-left px-2.5 py-1.5 hover:bg-emerald-50 text-xs"
                    data-testid={`or-import-client-pick-${c.id}`}
                  >
                    <span className="font-semibold">
                      {c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim() || "—"}
                    </span>
                    <span className="text-gray-500 ml-2">
                      {[c.tel_principal || c.telephone, c.email, c.ville].filter(Boolean).join(" · ")}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Prénom</Label>
            <Input value={client.payload.prenom || ""} onChange={(e) => updateClient("prenom", e.target.value)} data-testid="or-import-client-prenom" />
          </div>
          <div><Label className="text-xs">Nom</Label>
            <Input value={client.payload.nom || ""} onChange={(e) => updateClient("nom", e.target.value)} data-testid="or-import-client-nom" />
          </div>
          <div><Label className="text-xs">Raison sociale (flotte / assurance)</Label>
            <Input value={client.payload.raison_sociale || ""} onChange={(e) => updateClient("raison_sociale", e.target.value)} />
          </div>
          <div><Label className="text-xs">Téléphone</Label>
            <Input value={client.payload.telephone || ""} onChange={(e) => updateClient("telephone", e.target.value)} />
          </div>
          <div><Label className="text-xs">Adresse</Label>
            <Input value={client.payload.adresse || ""} onChange={(e) => updateClient("adresse", e.target.value)} />
          </div>
          <div><Label className="text-xs">NPA / Ville</Label>
            <div className="flex gap-2">
              <Input className="w-24" placeholder="NPA" value={client.payload.code_postal || ""} onChange={(e) => updateClient("code_postal", e.target.value)} />
              <Input placeholder="Ville" value={client.payload.ville || ""} onChange={(e) => updateClient("ville", e.target.value)} />
            </div>
          </div>
        </div>
      </section>

      {/* Véhicule */}
      <section className="border rounded-lg p-3 bg-white" data-testid="or-import-preview-vehicule">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Car className="w-4 h-4 text-[#52a675]" /> Véhicule
          </h3>
          {vehicule.match_type === "existing" ? (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              <CheckCircle2 className="w-3 h-3 mr-1 inline" /> Véhicule existant · {vehicule.match_reason}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              <AlertCircle className="w-3 h-3 mr-1 inline" /> Nouveau véhicule
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">Marque</Label>
            <Input value={vehicule.payload.marque || ""} onChange={(e) => updateVehicule("marque", e.target.value)} />
          </div>
          <div className="col-span-2"><Label className="text-xs">Modèle</Label>
            <Input value={vehicule.payload.modele || ""} onChange={(e) => updateVehicule("modele", e.target.value)} />
          </div>
          <div><Label className="text-xs">Plaque</Label>
            <Input value={vehicule.payload.plaque || ""} onChange={(e) => updateVehicule("plaque", e.target.value.toUpperCase())} />
          </div>
          <div><Label className="text-xs">VIN</Label>
            <Input value={vehicule.payload.vin || ""} onChange={(e) => updateVehicule("vin", e.target.value.toUpperCase())} />
          </div>
          <div><Label className="text-xs">Kilométrage</Label>
            <Input type="number" value={vehicule.payload.km_dernier ?? ""} onChange={(e) => updateVehicule("km_dernier", e.target.value ? parseInt(e.target.value, 10) : null)} />
          </div>
          {source === "guido" && (
            <div className="col-span-3"><Label className="text-xs">N° flotte Guido (CHXXXXX)</Label>
              <Input value={vehicule.payload.numero_flotte || ""} onChange={(e) => updateVehicule("numero_flotte", e.target.value)} />
            </div>
          )}
        </div>
      </section>

      {/* Meta OR */}
      {source === "auto-i" && (
        <section className="border rounded-lg p-3 bg-blue-50/50 border-blue-200">
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-blue-600" /> Dossier assurance
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-gray-500">N° sinistre :</span> <b>{or_meta.sinistre_numero || "—"}</b></div>
            <div><span className="text-gray-500">Assurance :</span> <b>{or_meta.assurance_nom || "—"}</b></div>
            {or_meta.police_numero && (
              <div><span className="text-gray-500">N° de police :</span> <b>{or_meta.police_numero}</b></div>
            )}
            <div data-testid="or-import-tarif">
              <span className="text-gray-500">Mode tarifaire :</span>{" "}
              <b>{or_meta.type_client_tarif === "assurance" ? "Assurance"
                : or_meta.type_client_tarif === "flotte" ? "Flotte" : "Particulier"}</b>
            </div>
          </div>
          {/* iter264 — Liaison assurance faite automatiquement quand la compagnie est au CRM */}
          <p className="text-xs mt-2" data-testid="or-import-assurance-link">
            {or_meta.assurance_id ? (
              <span className="text-emerald-700">
                ✓ Assurance liée au dossier : <b>{or_meta.assurance_match_label || or_meta.assurance_nom}</b>
              </span>
            ) : or_meta.assurance_nom ? (
              <span className="text-orange-700">
                Compagnie non enregistrée dans le CRM : l&apos;OR reste en mode assurance, la liaison
                pourra être faite depuis la fiche OR.
              </span>
            ) : (
              <span className="text-gray-600">
                Aucune assurance dans le rapport : l&apos;OR est créé au tarif du client.
              </span>
            )}
          </p>
        </section>
      )}
      {source === "guido" && (
        <section className="border rounded-lg p-3 bg-blue-50/50 border-blue-200">
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-blue-600" /> Références Guido
          </h3>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><span className="text-gray-500">Intervention :</span> <b>{or_meta.intervention_numero || "—"}</b></div>
            <div><span className="text-gray-500">Autorisation :</span> <b>{or_meta.autorisation_numero || "—"}</b></div>
            <div><span className="text-gray-500">Véhicule Guido :</span> <b>{or_meta.guido_vehicule_id || "—"}</b></div>
          </div>
        </section>
      )}

      {/* Lignes OR */}
      <section className="border rounded-lg p-3 bg-white" data-testid="or-import-preview-lignes">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Lignes de l&apos;OR ({or_lignes.length})</h3>
          <Button size="sm" variant="outline" onClick={addLigne} data-testid="or-import-add-ligne">
            <Plus className="w-3.5 h-3.5 mr-1" /> Ligne
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500 uppercase">
              <tr>
                <th className="px-2 py-1 text-left">Type</th>
                <th className="px-2 py-1 text-left">Libellé</th>
                <th className="px-2 py-1 text-right">Qté</th>
                <th className="px-2 py-1">Unité</th>
                <th className="px-2 py-1 text-right">PU HT</th>
                <th className="px-2 py-1 text-right">Total HT</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {or_lignes.map((l, i) => {
                const totLine = l.type_ligne === "texte" ? 0 : Number(l.quantite || 0) * Number(l.prix_unitaire_ht || 0);
                return (
                <tr key={i} data-testid={`or-import-ligne-${i}`}>
                  <td className="px-2 py-1">
                    <select
                      value={l.type_ligne}
                      onChange={(e) => updateLigne(i, "type_ligne", e.target.value)}
                      className="text-xs border rounded px-1 py-0.5 bg-white"
                    >
                      <option value="texte">Texte</option>
                      <option value="article">Article</option>
                      <option value="main_oeuvre">MO</option>
                      <option value="libre">Libre</option>
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <Input className="h-7 text-xs" value={l.libelle} onChange={(e) => updateLigne(i, "libelle", e.target.value)} />
                  </td>
                  <td className="px-2 py-1 w-20">
                    <Input type="number" step="0.01" className="h-7 text-xs text-right"
                      value={l.quantite}
                      onChange={(e) => updateLigne(i, "quantite", parseFloat(e.target.value) || 0)}
                      disabled={l.type_ligne === "texte"} />
                  </td>
                  <td className="px-2 py-1 w-20">
                    <select value={l.unite || ""} onChange={(e) => updateLigne(i, "unite", e.target.value)}
                      className="text-xs border rounded px-1 py-0.5 bg-white w-full" disabled={l.type_ligne === "texte"}>
                      <option value="">—</option>
                      <option value="forfait">forfait</option>
                      <option value="h">h</option>
                      <option value="piece">piece</option>
                      <option value="litre">L</option>
                      <option value="kg">kg</option>
                      <option value="m">m</option>
                      <option value="jeu">jeu</option>
                    </select>
                  </td>
                  <td className="px-2 py-1 w-24">
                    <Input type="number" step="0.01" className="h-7 text-xs text-right"
                      value={l.prix_unitaire_ht}
                      onChange={(e) => updateLigne(i, "prix_unitaire_ht", parseFloat(e.target.value) || 0)}
                      disabled={l.type_ligne === "texte"} />
                  </td>
                  <td className="px-2 py-1 text-right font-mono">{fmt(totLine)}</td>
                  <td className="px-2 py-1">
                    <button type="button" onClick={() => deleteLigne(i)} className="text-red-600 hover:text-red-800" data-testid={`or-import-delete-ligne-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-semibold">
                <td colSpan={5} className="px-2 py-2 text-right">Total HT calculé :</td>
                <td className="px-2 py-2 text-right font-mono">{fmt(totalHT)} CHF</td>
                <td></td>
              </tr>
              {totaux_hints?.total_ht != null && Math.abs(totalHT - Number(totaux_hints.total_ht)) > 0.05 && (
                <tr>
                  <td colSpan={7} className="px-2 py-1 text-[11px] text-orange-700 bg-orange-50 border-t border-orange-200">
                    ⚠ Écart avec le total du rapport ({fmt(totaux_hints.total_ht)} CHF). Vérifiez les lignes ci-dessus.
                  </td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </section>

      {/* iter270 — Un dossier ouvert existe déjà pour ce véhicule */}
      {doublon && (
        <div
          className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
          data-testid="or-import-doublon"
        >
          <div className="font-semibold">Dossier déjà ouvert pour ce véhicule</div>
          <p className="mt-0.5 text-[13px]">{doublon.message}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              size="sm" variant="outline"
              className="border-amber-400 text-amber-900"
              onClick={onIgnoreDoublon}
              data-testid="or-import-doublon-annuler"
            >
              Je reprends l&apos;OR {doublon.or_numero}
            </Button>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={onForceDoublon}
              data-testid="or-import-doublon-forcer"
            >
              Créer un second dossier quand même
            </Button>
          </div>
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Retour</Button>
        <Button
          onClick={onConfirm}
          disabled={clientManuel}
          title={clientManuel ? "Sélectionnez d'abord le client du dossier" : undefined}
          className="bg-[#52a675] hover:bg-[#3d8a5c]"
          data-testid="or-import-confirm"
        >
          Créer l&apos;OR
        </Button>
      </DialogFooter>
    </div>
  );
}
